import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { userOptionSelector } from 'store/auth/selectors';
import { rentListSelector, rentArchiveListSelector, reserveListSelector } from 'store/transferAct/selectors';
import { Rent, Reservation } from 'store/transferAct/actions';
import { useDebounce } from 'hooks/useDebounce';
import { usePageVisibility } from 'hooks/useVisibility';
import ReactTooltip from 'react-tooltip';
import { Stock } from 'store/stock/actions';
import { Icon } from 'components/Icon';
import { ButtonLabel } from 'components/Button';
import { RentColumn } from './RentColumn';
import { ReservationColumn } from './ReservationColumn';
import { useFirstRender } from 'hooks/useFirstRender';

const defaultLimit = 15;

export const queryTypes = {
  ArchiveReserve: 'ArchiveReserve',
  ActualReserve: 'ActualReserve',
  WaitingRent: 'WaitingRent',
  PendingRent: 'PendingRent',
  ActiveRent: 'ActiveRent',
  DebtRent: 'DebtRent',
  ExpireRent: 'ExpireRent',
  ArchiveRent: 'ArchiveRent',
};

export const rentItems = [
  { status: 'Ожидает обработки', classColor: 'column-button--gr', query: queryTypes.WaitingRent },
  { status: 'Ожидает выдачи инструмента', classColor: 'column-button--bl', query: queryTypes.PendingRent },
  { status: 'В аренде', classColor: 'column-button--ye', query: queryTypes.ActiveRent },
  { status: 'Истекает срок', classColor: 'column-button--or', query: queryTypes.ExpireRent },
  { status: 'Долг', classColor: 'column-button--gr', query: queryTypes.DebtRent },
];

const items = {
  legal: 'Юр. лица',
  individ: 'Физ. лица',
};

const ColumnsContent = ({ stock, search, itemSearch, itemQueryParam }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const debounceSearch = useDebounce(search, 500, true);
  const isVisible = usePageVisibility();
  const [isApprovedState, setIsApprovedState] = useState('');
  const reserveList = useSelector(reserveListSelector);
  const rentList = useSelector(rentListSelector);
  const rentArchiveList = useSelector(rentArchiveListSelector);
  const userOption = useSelector(userOptionSelector);
  const [reserveArchive, setReserveArchive] = useState(false);
  const [rentArchive, setRentArchive] = useState(false);
  const [calendarVisible, setCalendarVisible] = useState(false);
  const firstRender = useFirstRender();

  useEffect(() => {
    if (stock || !!debounceSearch.string || (itemQueryParam && !itemSearch)) {
      loadTransferActs();
    }
  }, [stock, debounceSearch, itemSearch]);

  useEffect(() => {
    ReactTooltip.rebuild();
  });

  useEffect(() => {
    if (!rentList || firstRender || itemSearch) {
      return;
    }
    const { string } = debounceSearch;

    if (isVisible && (!string || !string.length)) {
      pingTransferActs();
      // dispatch(Stock.pingStockList());
    }
  }, [isVisible]);

  const checkQuery = (query, subQueries, lazy = false) => {
    const { item, string } = debounceSearch;
    let newQuery = { ...query };
    if (string?.length > 0) {
      newQuery.isApproved = reserveArchive ? isApprovedState : 0;
      if (!lazy) {
        subQueries.push(queryTypes.ArchiveRent);
      }
      newQuery.sub_queries = subQueries.join(',');
      if (item === items.individ) {
        newQuery.individual = true;
        newQuery.search = string;
      } else if (item === items.legal) {
        newQuery.individual = false;
        newQuery.search = string;
      }
    }
    if (!!itemSearch) {
      newQuery.itemID = itemSearch.itemID;
    }
    return newQuery;
  };

  const loadTransferActs = async () => {
    let subQueries = [
      queryTypes.WaitingRent,
      queryTypes.PendingRent,
      queryTypes.ActiveRent,
      queryTypes.DebtRent,
      queryTypes.ExpireRent,
      reserveArchive ? queryTypes.ArchiveReserve : queryTypes.ActualReserve,
    ];

    let query = checkQuery(
      {
        limit: defaultLimit,
        stockID: stock?.stockID1C,
        sub_queries: subQueries.join(','),
      },
      subQueries
    );
    dispatch(Rent.getSortedList(query));
  };

  const pingTransferActs = async () => {
    setReserveArchive(false);
    setRentArchive(false);

    const query = {
      limit: defaultLimit,
      stockID: stock?.stockID1C,
      sub_queries: [
        queryTypes.PendingRent,
        queryTypes.ActiveRent,
        queryTypes.DebtRent,
        queryTypes.ExpireRent,
        queryTypes.WaitingRent,
        queryTypes.ActualReserve,
      ].join(','),
    };
    dispatch(Rent.getUpdatedList(query));
  };

  const handleFilterIcon = (isApproved) => {
    setIsApprovedState(isApproved);
    dispatch(
      Reservation.getReserveList({
        archive: true,
        sub_queries: queryTypes.ArchiveReserve,
        isApproved,
        stock: stock?.stockID1C,
        limit: 10,
      })
    );
  };

  const handleReserveArchive = () => {
    dispatch(
      Reservation.getReserveList({
        archive: !reserveArchive,
        sub_queries: !reserveArchive ? queryTypes.ArchiveReserve : queryTypes.ActualReserve,
        stock: stock?.stockID1C,
        limit: 10,
      })
    );

    setReserveArchive(!reserveArchive);
    setCalendarVisible(false);
  };

  const handleFilterByDate = ({ from, to } = {}) => {
    setCalendarVisible(false);

    if (!from & !to) {
      dispatch(
        Reservation.getReserveList({
          archive: true,
          stock: stock?.stockID1C,
          limit: defaultLimit,
          sub_queries: queryTypes.ArchiveReserve,
        })
      );
    } else {
      dispatch(
        Reservation.getReserveList({
          archive: true,
          stock: stock?.stockID1C,
          limit: defaultLimit,
          sub_queries: queryTypes.ArchiveReserve,
          startDateTime: Math.round(+from / 1000),
          endDateTime: Math.round(+to / 1000),
        })
      );
    }
  };

  const handleRentArchive = () => {
    dispatch(
      Rent.getRentArchiveList({
        stock: stock?.stockID1C,
        limit: defaultLimit,
        sub_queries: !rentArchive ? queryTypes.ArchiveRent : '',
      })
    );
    setRentArchive(!rentArchive);
  };

  const handleAddRent = () => {
    history.push('/rent');
  };

  const handleScrollColumn = (query) => {
    return dispatch(
      Rent.addRentsToList(
        checkQuery({ ...query, stockID: stock?.stockID1C, limit: defaultLimit }, [query.sub_queries], true)
      )
    );
  };

  return (
    <>
      <ReservationColumn
        reserveArchive={reserveArchive}
        setReserveArchive={setReserveArchive}
        handleFilterIcon={handleFilterIcon}
        handleFilterByDate={handleFilterByDate}
        handleReserveArchive={handleReserveArchive}
        stock={stock}
        reserveList={reserveList}
        limit={defaultLimit}
        userOption={userOption}
        onScrollColumn={handleScrollColumn}
        calendarVisible={calendarVisible}
        setCalendarVisible={setCalendarVisible}
      />

      {rentItems.map((item) => (
        <RentColumn
          key={item.status}
          stock={stock}
          item={item}
          limit={defaultLimit}
          rentList={rentList?.filter((rent) => rent.status === item.status)}
          button={
            item.status === 'Ожидает обработки' && (
              <ButtonLabel
                className="column-button--gr btn-clickable"
                onClick={handleAddRent}
                text={<Icon name="add" tooltipText="Добавить аренду" />}
              />
            )
          }
          onScrollColumn={handleScrollColumn}
          userOption={userOption}
        />
      ))}

      <RentColumn
        item={{ status: 'Завершено', classColor: 'column-button--gr', query: queryTypes.ArchiveRent }}
        rentList={rentArchiveList}
        limit={defaultLimit}
        stock={stock}
        button={
          <ButtonLabel rounded={true} onClick={handleRentArchive} text={rentArchive ? 'Скрыть архив' : 'Архив'} />
        }
        onScrollColumn={handleScrollColumn}
        userOption={userOption}
      />
      <ReactTooltip id="comments-table" place="bottom" effect="solid" />

      <ReactTooltip id="commentary" place="bottom" effect="solid" />
      <ReactTooltip id="instruments" place="bottom" effect="solid" />
    </>
  );
};

ColumnsContent.propTypes = {
  stock: PropTypes.any,
  itemSearch: PropTypes.object,
  itemQueryParam: PropTypes.string,
  search: PropTypes.shape({
    item: PropTypes.string,
    string: PropTypes.string,
  }),
};

export { ColumnsContent };
